import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { LuMessageSquare } from "react-icons/lu";
import { AppContext } from "../../utils/Context/AppContext";
import { clearAllModelResponses } from "../../utils/helpers/modelResponsesStorage";
import {
  searchUsers,
  listAdminUsers,
  setAdminStatus,
  getUsageSummary,
  getSemanticCacheEnabled,
  setSemanticCacheEnabled,
  getSemanticCacheThreshold,
  setSemanticCacheThreshold,
  clearSemanticCache,
  getSemanticCacheExclusions,
  addSemanticCacheExclusion,
  deleteSemanticCacheExclusion,
} from "../../utils/services/api";
import CROSS_ICON from "../../assets/close_icon.svg";

const Settings = () => {
  const { isAdmin } = useContext(AppContext);

  // --- Cache (admin only) ---
  const [clearing, setClearing] = useState(false);
  const handleClearCache = async () => {
    setClearing(true);
    // Clears both: this browser's locally-stored multi-model comparison
    // responses, AND the actual server-side semantic cache index
    // (neca-semantic-cache in Azure AI Search) that /default_chat reads from.
    clearAllModelResponses();
    try {
      const result = await clearSemanticCache();
      toast.success(`Cache cleared (${result.deleted} cached entries removed)`);
    } catch (error) {
      toast.error("Cleared local cache, but failed to clear server cache");
    } finally {
      setClearing(false);
    }
  };

  // --- Semantic cache (admin only) ---
  const [semanticCacheEnabled, setSemanticCacheEnabledLocal] = useState(false);
  const [savingSemanticCacheEnabled, setSavingSemanticCacheEnabled] = useState(false);
  const [thresholdInput, setThresholdInput] = useState(95);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [loadingSemanticCache, setLoadingSemanticCache] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    const loadSemanticCache = async () => {
      try {
        setLoadingSemanticCache(true);
        const [enabled, threshold] = await Promise.all([
          getSemanticCacheEnabled(),
          getSemanticCacheThreshold(),
        ]);
        setSemanticCacheEnabledLocal(enabled);
        if (threshold !== null && threshold !== undefined) setThresholdInput(threshold);
      } catch (error) {
        toast.error("Failed to load semantic cache settings");
      } finally {
        setLoadingSemanticCache(false);
      }
    };
    loadSemanticCache();
  }, [isAdmin]);

  const handleToggleSemanticCache = async (checked) => {
    try {
      setSavingSemanticCacheEnabled(true);
      await setSemanticCacheEnabled(checked);
      setSemanticCacheEnabledLocal(checked);
      toast.success(checked ? "Semantic cache enabled" : "Semantic cache disabled");
    } catch (error) {
      toast.error("Failed to update semantic cache setting");
    } finally {
      setSavingSemanticCacheEnabled(false);
    }
  };

  const handleSaveThreshold = async () => {
    try {
      setSavingThreshold(true);
      await setSemanticCacheThreshold(Number(thresholdInput));
      toast.success(`Similarity threshold set to ${thresholdInput}`);
    } catch (error) {
      toast.error("Failed to update similarity threshold");
    } finally {
      setSavingThreshold(false);
    }
  };

  // --- Semantic cache exclusion terms (admin only) ---
  // Some questions read as near-identical to the embedding model but need
  // genuinely different answers (e.g. "Type A RCD" vs "Type B RCD"), so no
  // similarity threshold can safely tell them apart. Rather than hardcode
  // every such case, admins can add the problem phrase here — any question
  // containing it (case-insensitive) is skipped by the cache entirely, on
  // both the read and write side.
  const [exclusions, setExclusions] = useState([]);
  const [newExclusion, setNewExclusion] = useState("");
  const [savingExclusion, setSavingExclusion] = useState(false);
  const [deletingExclusionId, setDeletingExclusionId] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    getSemanticCacheExclusions().then(setExclusions);
  }, [isAdmin]);

  const handleAddExclusion = async (e) => {
    e.preventDefault();
    const term = newExclusion.trim();
    if (!term) return;
    try {
      setSavingExclusion(true);
      const updated = await addSemanticCacheExclusion(term);
      setExclusions(updated);
      setNewExclusion("");
      toast.success(`"${term}" added to cache exclusions`);
    } catch (error) {
      toast.error("Failed to add exclusion term");
    } finally {
      setSavingExclusion(false);
    }
  };

  const handleDeleteExclusion = async (id) => {
    try {
      setDeletingExclusionId(id);
      const updated = await deleteSemanticCacheExclusion(id);
      setExclusions(updated);
    } catch (error) {
      toast.error("Failed to remove exclusion term");
    } finally {
      setDeletingExclusionId(null);
    }
  };

  // --- User management (admin only — moved here from the old standalone
  // Admin Panel page) ---
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Clicked-user detail popup: Graph profile fields (already included in the
  // search response) + their usage stats (fetched on open, since it's a
  // separate, potentially-empty-for-most-users lookup).
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserUsage, setSelectedUserUsage] = useState([]);
  const [loadingSelectedUserUsage, setLoadingSelectedUserUsage] = useState(false);

  const openUserDetail = async (user) => {
    setSelectedUser(user);
    setSelectedUserUsage([]);
    // Lowercased: usage is logged under whatever email casing the Azure AD
    // JWT claims provide, which doesn't reliably match Graph's casing here —
    // the backend now matches case-insensitively too, this is just belt-and-suspenders.
    const email = (user.mail || user.userPrincipalName || "").toLowerCase();
    if (!email) return;
    try {
      setLoadingSelectedUserUsage(true);
      setSelectedUserUsage(await getUsageSummary(email));
    } catch (error) {
      toast.error("Failed to load usage for this user");
    } finally {
      setLoadingSelectedUserUsage(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      setLoadingAdmins(true);
      setAdminUsers(await listAdminUsers());
    } catch (error) {
      toast.error("Failed to load admin list");
    } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    try {
      setSearching(true);
      const results = await searchUsers(query.trim());
      setSearchResults(results);
      if (results.length === 0) {
        toast("No matching users found", { icon: "ℹ️" });
      }
    } catch (error) {
      toast.error("User search failed — the directory permission may still be propagating");
    } finally {
      setSearching(false);
    }
  };

  const isAlreadyAdmin = (email) =>
    adminUsers.some((u) => u.Email?.toLowerCase() === email?.toLowerCase() && u.IsAdmin);

  const handleToggleAdmin = async (email, displayName, nextIsAdmin) => {
    try {
      setPendingEmail(email);
      await setAdminStatus(email, nextIsAdmin, displayName);
      toast.success(nextIsAdmin ? `${email} granted admin access` : `${email} admin access revoked`);
      await loadAdminUsers();
    } catch (error) {
      toast.error("Failed to update admin status");
    } finally {
      setPendingEmail(null);
    }
  };

  // Manual admin add — for accounts "Search users" (Graph people search)
  // can't find, e.g. shared/generic mailboxes like ct.nsw@neca.asn.au, or
  // an account whose directory permission is still propagating. Writes
  // straight to SQL via the same setAdminStatus endpoint the search-result
  // "Grant admin" button already uses, with no displayName since there's
  // no Graph profile to pull one from.
  const [manualEmail, setManualEmail] = useState("");
  const [addingManual, setAddingManual] = useState(false);

  const handleAddManualAdmin = async (e) => {
    e.preventDefault();
    const email = manualEmail.trim().toLowerCase();
    if (!email) return;
    try {
      setAddingManual(true);
      await setAdminStatus(email, true, null);
      toast.success(`${email} granted admin access`);
      setManualEmail("");
      await loadAdminUsers();
    } catch (error) {
      toast.error("Failed to grant admin access");
    } finally {
      setAddingManual(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 h-full w-full bg-slate-100 px-4 py-6 overflow-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[#3A3A3A]">Settings</h1>
        <Link
          to="/chat?new_conversation"
          className="text-[#00AEEF] text-sm flex items-center gap-2 font-semibold px-3 py-2 hover:bg-[#00AEEF] hover:text-white bg-white rounded-[4px] border border-[#00AEEF] transition-colors duration-300 ease-in-out"
        >
          <LuMessageSquare size={16} />
          Chat
        </Link>
      </div>

      {/* Cache — admin only */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
          <h2 className="font-semibold text-[#3A3A3A] mb-2">Cache</h2>
          <p className="text-sm text-[#616161] mb-4">
            Clears the multi-model response data cached in this browser, and wipes the
            semantic cache used by chat so no old cached answers get served. Useful if a
            model's answer looks stale or stuck.
          </p>
          <button
            onClick={handleClearCache}
            disabled={clearing}
            className="bg-[#00AEEF] hover:bg-[#3aa7cf] text-white px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50"
          >
            {clearing ? "Clearing…" : "Clear cache"}
          </button>
        </div>
      )}

      {/* Semantic cache (server-side, /default_chat) — admin only */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
          <h2 className="font-semibold text-[#3A3A3A] mb-2">Semantic cache</h2>
          <p className="text-sm text-[#616161] mb-4">
            Reuses a previous answer for a similar question in the same state, to save
            cost. Off by default — it was found to occasionally serve a cached answer
            for a genuinely different question, so treat the threshold as
            experimental until verified with real testing.
          </p>
          {loadingSemanticCache ? (
            <p className="text-sm text-[#616161]">Loading…</p>
          ) : (
            <div className="flex flex-col gap-4">
              <label className="flex items-center gap-2 text-sm text-[#242424]">
                <input
                  type="checkbox"
                  checked={semanticCacheEnabled}
                  onChange={(e) => handleToggleSemanticCache(e.target.checked)}
                  disabled={savingSemanticCacheEnabled}
                />
                Enable semantic cache for /default_chat
              </label>
              <div className="flex items-center gap-3">
                <label className="text-sm text-[#616161]">Similarity threshold</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={thresholdInput}
                  onChange={(e) => setThresholdInput(e.target.value)}
                  className="w-20 border border-[#DCDCDC] rounded-full px-3 py-1 text-sm outline-none focus:border-[#00AEEF]"
                />
                <button
                  onClick={handleSaveThreshold}
                  disabled={savingThreshold}
                  className="bg-[#00AEEF] hover:bg-[#3aa7cf] text-white px-4 py-1.5 rounded-full text-sm font-semibold disabled:opacity-50"
                >
                  {savingThreshold ? "Saving…" : "Save"}
                </button>
              </div>

              <div className="border-t border-[#EEEBEB] pt-4">
                <label className="text-sm font-semibold text-[#242424]">
                  Exclude questions from caching
                </label>
                <p className="text-xs text-[#616161] mt-1 mb-3">
                  Any question containing one of these phrases (case-insensitive)
                  is never read from or written to the cache — for questions that
                  read as near-identical to the model but need different answers
                  (e.g. "Type A RCD" vs "Type B RCD").
                </p>
                <form onSubmit={handleAddExclusion} className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newExclusion}
                    onChange={(e) => setNewExclusion(e.target.value)}
                    placeholder="e.g. When is a Type A RCD required?"
                    className="flex-1 border border-[#DCDCDC] rounded-full px-4 py-1.5 text-sm outline-none focus:border-[#00AEEF]"
                  />
                  <button
                    type="submit"
                    disabled={savingExclusion || !newExclusion.trim()}
                    className="bg-[#00AEEF] hover:bg-[#3aa7cf] text-white px-4 py-1.5 rounded-full text-sm font-semibold disabled:opacity-50"
                  >
                    {savingExclusion ? "Adding…" : "Add"}
                  </button>
                </form>
                {exclusions.length > 0 && (
                  <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
                    {exclusions.map((ex) => (
                      <div
                        key={ex.id}
                        className="flex items-center justify-between border border-[#EEEBEB] rounded-full px-4 py-1.5 shrink-0"
                      >
                        <span className="text-sm text-[#242424]">{ex.term}</span>
                        <button
                          onClick={() => handleDeleteExclusion(ex.id)}
                          disabled={deletingExclusionId === ex.id}
                          className="text-xs font-semibold text-red-500 hover:underline disabled:opacity-50"
                        >
                          {deletingExclusionId === ex.id ? "Removing…" : "Remove"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* User management — admin only */}
      {isAdmin && (
        <>
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
            <h2 className="font-semibold text-[#3A3A3A] mb-3">Search users</h2>
            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or email"
                className="flex-1 border border-[#DCDCDC] rounded-full px-4 py-2 text-sm outline-none focus:border-[#00AEEF]"
              />
              <button
                type="submit"
                disabled={searching}
                className="bg-[#00AEEF] hover:bg-[#3aa7cf] text-white px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50"
              >
                {searching ? "Searching…" : "Search"}
              </button>
            </form>

            {searchResults.length > 0 && (
              <div className="flex flex-col gap-2">
                {searchResults.map((u) => {
                  const email = u.mail || u.userPrincipalName;
                  const alreadyAdmin = isAlreadyAdmin(email);
                  return (
                    <div
                      key={u.id}
                      onClick={() => openUserDetail(u)}
                      className="flex items-center justify-between border border-[#EEEBEB] rounded-md px-4 py-2 cursor-pointer hover:bg-[#FAFEFF]"
                    >
                      <div>
                        <p className="text-sm font-semibold text-[#242424]">{u.displayName}</p>
                        <p className="text-xs text-[#616161]">{email}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAdmin(email, u.displayName, !alreadyAdmin);
                        }}
                        disabled={pendingEmail === email}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border disabled:opacity-50 ${
                          alreadyAdmin
                            ? "border-red-400 text-red-500 hover:bg-red-50"
                            : "border-[#00AEEF] text-[#00AEEF] hover:bg-[#EFFBFF]"
                        }`}
                      >
                        {alreadyAdmin ? "Revoke admin" : "Grant admin"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
            <h2 className="font-semibold text-[#3A3A3A] mb-3">
              Current admins {!loadingAdmins && `(${adminUsers.filter((u) => u.IsAdmin).length})`}
            </h2>
            <form onSubmit={handleAddManualAdmin} className="flex gap-2 mb-4">
              <input
                type="text"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                placeholder="Add admin by email (e.g. shared mailboxes Search users can't find)"
                className="flex-1 border border-[#DCDCDC] rounded-full px-4 py-2 text-sm outline-none focus:border-[#00AEEF]"
              />
              <button
                type="submit"
                disabled={addingManual || !manualEmail.trim()}
                className="bg-[#00AEEF] hover:bg-[#3aa7cf] text-white px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50"
              >
                {addingManual ? "Adding…" : "Add admin"}
              </button>
            </form>
            {loadingAdmins ? (
              <p className="text-sm text-[#616161]">Loading…</p>
            ) : adminUsers.filter((u) => u.IsAdmin).length === 0 ? (
              <p className="text-sm text-[#616161]">No admins yet.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {adminUsers
                  .filter((u) => u.IsAdmin)
                  .map((u) => (
                    <div
                      key={u.Id}
                      className="flex items-center justify-between border border-[#EEEBEB] rounded-md px-4 py-2"
                    >
                      <div>
                        <p className="text-sm font-semibold text-[#242424]">
                          {u.DisplayName || u.Email}
                        </p>
                        <p className="text-xs text-[#616161]">{u.Email}</p>
                      </div>
                      <button
                        onClick={() => handleToggleAdmin(u.Email, u.DisplayName, false)}
                        disabled={pendingEmail === u.Email}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full border border-red-400 text-red-500 hover:bg-red-50 disabled:opacity-50"
                      >
                        Revoke admin
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </>
      )}

      {selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setSelectedUser(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#EEEBEB]">
              <h2 className="font-semibold text-[#3A3A3A]">User details</h2>
              <img
                src={CROSS_ICON}
                alt="Close"
                className="cursor-pointer h-4 w-4"
                onClick={() => setSelectedUser(null)}
              />
            </div>
            <div className="p-4 flex flex-col gap-4">
              <div>
                <p className="text-base font-semibold text-[#242424]">{selectedUser.displayName}</p>
                <p className="text-sm text-[#616161]">{selectedUser.mail || selectedUser.userPrincipalName}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[#616161] text-xs">Job title</p>
                  <p className="text-[#242424]">{selectedUser.jobTitle || "Not set"}</p>
                </div>
                <div>
                  <p className="text-[#616161] text-xs">Department</p>
                  <p className="text-[#242424]">{selectedUser.department || "Not set"}</p>
                </div>
                <div>
                  <p className="text-[#616161] text-xs">Office location</p>
                  <p className="text-[#242424]">{selectedUser.officeLocation || "Not set"}</p>
                </div>
              </div>

              <div className="border-t border-[#EEEBEB] pt-3">
                <p className="font-semibold text-sm text-[#3A3A3A] mb-2">Usage</p>
                {loadingSelectedUserUsage ? (
                  <p className="text-sm text-[#616161]">Loading…</p>
                ) : selectedUserUsage.length === 0 ? (
                  <p className="text-sm text-[#616161]">No usage recorded yet for this user.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-6">
                      <div>
                        <p className="text-xl font-bold text-[#3A3A3A]">
                          {selectedUserUsage.reduce((sum, r) => sum + (r.total_tokens || 0), 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-[#616161]">Total tokens</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-[#3A3A3A]">
                          {selectedUserUsage.reduce((sum, r) => sum + (r.query_count || 0), 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-[#616161]">Total queries</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {selectedUserUsage.map((r) => (
                        <div
                          key={r.model}
                          className="flex items-center justify-between text-xs border border-[#EEEBEB] rounded-md px-3 py-1.5"
                        >
                          <span className="text-[#242424] font-medium">{r.model}</span>
                          <span className="text-[#616161]">
                            {(r.total_tokens || 0).toLocaleString()} tokens · {r.query_count || 0} queries
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
